import { create } from 'zustand'
import type { ItemType, MarketPriceInfo, WalletInfo, Villager, VillageProfileType } from '@/economy/types'
import type { StructureType } from '@/world/villages/types'

export interface InteractionTarget {
  villageIndex: number
  structureIndex: number
  structureType: StructureType
  distance: number
  villageName: string
  serverId: number | null  // null if village has no server market
  profileType: VillageProfileType | null
}

export interface VillageEconomyData {
  villagers: Villager[]
  marketPrices: MarketPriceInfo[] | null
  lastPriceFetch: number
}

interface EconomyState {
  // Player wallet
  wallet: WalletInfo | null
  walletLoading: boolean

  // Active interaction
  nearestTarget: InteractionTarget | null  // what's in range (for HUD prompt)
  activeInteraction: InteractionTarget | null  // panel is open for this target
  panelOpen: boolean

  // Village economy data (keyed by village index)
  villageData: Map<number, VillageEconomyData>

  // Trade state
  tradeLoading: boolean
  tradeError: string | null

  // Actions
  setNearestTarget: (target: InteractionTarget | null) => void
  openPanel: () => void
  closePanel: () => void
  setWallet: (wallet: WalletInfo) => void
  setWalletLoading: (loading: boolean) => void
  setMarketPrices: (villageIndex: number, prices: MarketPriceInfo[]) => void
  setVillageData: (villageIndex: number, data: VillageEconomyData) => void
  setTradeLoading: (loading: boolean) => void
  setTradeError: (error: string | null) => void
  updateCargoFromTrade: (item: ItemType, newQty: number, newFollis: number) => void
}

export const useEconomyStore = create<EconomyState>((set, get) => ({
  wallet: null,
  walletLoading: false,
  nearestTarget: null,
  activeInteraction: null,
  panelOpen: false,
  villageData: new Map(),
  tradeLoading: false,
  tradeError: null,

  setNearestTarget: (target) => set({ nearestTarget: target }),

  openPanel: () => {
    const { nearestTarget } = get()
    if (nearestTarget) {
      set({ activeInteraction: nearestTarget, panelOpen: true, tradeError: null })
    }
  },

  closePanel: () => set({ activeInteraction: null, panelOpen: false, tradeError: null }),

  setWallet: (wallet) => set({ wallet }),
  setWalletLoading: (loading) => set({ walletLoading: loading }),

  setMarketPrices: (villageIndex, prices) => {
    const { villageData } = get()
    const existing = villageData.get(villageIndex)
    const updated = new Map(villageData)
    updated.set(villageIndex, {
      villagers: existing?.villagers ?? [],
      marketPrices: prices,
      lastPriceFetch: Date.now(),
    })
    set({ villageData: updated })
  },

  setVillageData: (villageIndex, data) => {
    const { villageData } = get()
    const updated = new Map(villageData)
    updated.set(villageIndex, data)
    set({ villageData: updated })
  },

  setTradeLoading: (loading) => set({ tradeLoading: loading }),
  setTradeError: (error) => set({ tradeError: error }),

  updateCargoFromTrade: (item, newQty, newFollis) => {
    const { wallet } = get()
    if (!wallet) return
    const newCargo = wallet.cargo.filter(([i]) => i !== item)
    if (newQty > 0) newCargo.push([item, newQty])
    set({
      wallet: {
        ...wallet,
        follis: newFollis,
        cargo: newCargo,
        total_follis_value: newFollis + wallet.solidus * 120,
      },
    })
  },
}))
